<template>
  <div class="pdf-page-viewer">
    <canvas
      id="page-renderer"
      ref="canvas"
      v-bind="canvasAttrs"
    ></canvas>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import type { PropType } from 'vue'
import type { RenderTask, PageViewport } from 'pdfjs-dist'
import type { PdfPage } from '@components/uploader/types'

const props = defineProps({
  page: {
    type: Object as PropType<PdfPage>,
    required: true,
  },
  scale: {
    type: Number,
    default: 1,
  },
})

const emit = defineEmits<{
  'page-rendered': [pageNumber: number]
  'page-render-error': [error: unknown]
}>()

let viewport: PageViewport = props.page.getViewport({ scale: props.scale })
let renderTask: RenderTask

const canvas = ref<HTMLCanvasElement | null>(null)

const canvasAttrs = computed(() => {
  const { width, height } = props.page.getViewport({ scale: props.scale })

  return {
    height,
    width,
  }
})

watch(() => props.page, () => {
  destroyPage() // FIXME: перенести менеджмент страниц в PDFViewer
  renderPage()
})

watch(() => props.scale, () => {
  renderPage()
})

const renderPage = () => {
  viewport = props.page.getViewport({ scale: props.scale })
  const canvasContext = canvas.value!.getContext('2d')!
  const renderContext = { canvasContext, viewport }

  if (renderTask) renderTask.cancel()

  renderTask = props.page.render(renderContext)

  renderTask.promise
    .then(() => {
      emit('page-rendered', props.page.pageNumber)
    })
    .catch((error) => {
      emit('page-render-error', error)
    })
}

const destroyPage = () => {
  props.page.cleanup()
}

onMounted(() => {
  renderPage()
})

onBeforeUnmount(() => {
  destroyPage()
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/scss/mixins';

.pdf-page-viewer {
  display: grid;
  height: 100%;
  width: 100%;
  overflow: auto;
  @include scroll;
  scrollbar-gutter: initial;
  background-color: var(--color-bg-secondary);
  place-items: center;

  @include narrow_tablet {
    padding: 12px 0;
  }

  canvas {
    border-radius: 8px;
  }

  @media (max-width: 743px) {
    border: none;
    border-radius: 0;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    canvas {
      border: none;
    }
  }
}
</style>
